import { api } from '@/config/api';

export interface UploadResult {
  url: string;
  fileId: string;
  name: string;
  size: number;
  fileType: string;
  height?: number;
  width?: number;
  thumbnailUrl?: string;
}

/** One previously uploaded file in the company's media library. */
export interface LibraryFile {
  fileId: string;
  name: string;
  url: string;
  thumbnailUrl?: string;
  filePath: string;
  fileType: 'image' | 'non-image';
  mime?: string;
  size: number;
  width?: number;
  height?: number;
  createdAt: string;
  tags?: string[];
}

/**
 * The server scopes Google media to slightor/<companyUid>/google-ads/<module>.
 * The library also reads older folders so existing campaign assets remain reusable.
 */
export const MEDIA_FOLDERS = {
  googleAppPromotion: 'google-ads/app_promotion',
  googleYoutube: 'google-ads/youtube',
  googleDisplay: 'google-ads/display',
  googlePmax: 'google-ads/pmax',
  catalog: 'catalog',
} as const;

export function validateWhatsAppMedia(file: { size: number; type: string }, folder?: string): string | null {
  if (!folder?.startsWith('whatsapp/')) return null;
  const kind = folder.split('/').at(-1);
  const limits: Record<string, { max: number; types: string[] }> = {
    images: { max: 5, types: ['image/jpeg', 'image/png'] },
    videos: { max: 16, types: ['video/mp4', 'video/3gpp'] },
    audio: { max: 16, types: ['audio/aac', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/ogg'] },
    documents: { max: 50, types: ['text/plain', 'application/pdf', 'application/msword', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'] },
  };
  const rule = limits[kind ?? ''];
  if (!rule) return 'Choose a WhatsApp media folder.';
  if (!rule.types.includes(file.type)) return 'This file format is not supported by WhatsApp for this media type.';
  if (file.size > rule.max * 1024 * 1024) return `Choose a file smaller than ${rule.max} MB.`;
  return null;
}

export const uploadsService = {
  /** Everything this company uploaded into an area, newest first. */
  library: async (folder: string, type: 'image' | 'video' | 'document' | 'audio' | 'all' = 'all', limit = 200): Promise<LibraryFile[]> =>
    (await api.get('/slighter_crm/uploads/library', { params: { folder, type, limit } })).data,
  deleteLibraryFile: async (fileId: string): Promise<{ ok: boolean; fileId: string }> =>
    (await api.delete(`/slighter_crm/uploads/library/${encodeURIComponent(fileId)}`)).data,

  /** Uploads a file via the backend ImageKit proxy. The server holds the secret. */
  imagekit: async (file: File, folder?: string, tags?: string): Promise<UploadResult> => {
    const issue = validateWhatsAppMedia(file, folder);
    if (issue) throw new Error(issue);
    const fd = new FormData();
    fd.append('file', file);
    if (folder) fd.append('folder', folder);
    if (tags) fd.append('tags', tags);
    const res = await api.post('/slighter_crm/uploads/imagekit', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Allow up to 50 MB; long timeout for big videos.
      timeout: 180_000,
    });
    return res.data;
  },
};
